import { Message } from 'ably';
import { useChannel as useAblyChannel, useChannelStateListener } from 'ably/react';
import { useContext, useEffect, useState } from 'react';

import AppContext from '@/contexts/appContext';

/**
 * Wrapper for Ably useChannel hook that returns websocket channel data
 * @param channelId
 * @param channelName
 * @param messageCallback
 * @param shouldDetach
 */
export const useChannelData = <T>({
  channelId,
  channelName,
  eventName,
  messageCallback,
  shouldDetach = true
}: {
  channelId: string;
  channelName: string;
  eventName?: string;
  messageCallback?: (message: Message) => void;
  shouldDetach?: boolean;
}) => {
  const { addMessage } = useContext(AppContext);
  const [channelData, setChannelData] = useState<T | null>(null);
  const [messageId, setMessageId] = useState<string | undefined>(undefined);

  const callbackOnMessage = (message: Message) => {
    const { id: messageId, encoding } = message;

    if (eventName && message.name !== eventName) return;

    // https://github.com/ably/ably-common/blob/main/test-resources/messages-encoding.json
    if (encoding == 'utf-8/cipher+aes-256-cbc') {
      // Ignore encrypted messages as they are redelivered as plaintext once ably decrypts them
      return;
    }

    try {
      const data = JSON.parse(message.data);
      setChannelData(data);
      setMessageId(messageId);
      messageCallback && messageCallback(message);
    } catch (error) {
      console.log(`Error parsing data from ${channelName} channel`, error);
      addMessage('error', `Error parsing data from ${channelName} channel`);
    }
  };

  const { ably, channel, channelError, connectionError } = useAblyChannel(
    {
      channelName: channelId
    },
    callbackOnMessage
  );

  useChannelStateListener(channelId, (newState) => {
    if (newState.current === 'attached') {
      console.log(`Successfully subscribed to ${channelName} channel`);
    }

    if (newState.current === 'detached') {
      console.log(`Successfully detached from ${channelName} channel`);
    }

    if (newState.current === 'failed') {
      console.error(`Failed to attach to ${channelName} channel`);
    }
  });

  useEffect(() => {
    return () => {
      if (shouldDetach && channel.state === 'attached') {
        (async () => {
          try {
            await channel.detach();
            console.log(`Successfully detached from channel ${channelName}`);
          } catch (err) {
            console.log(`Failed to detach from channel ${channelName}`, err);
          }
        })();
      }
    };
  }, [ably.channels, channel, channelId, channelName, shouldDetach]);

  useEffect(() => {
    if (channel.state === 'attached' && channelData) {
      console.log(`Received new data from  ${channelName}`, channelData);
    }
  }, [addMessage, channel.state, channelData, channelName, messageId]);

  useEffect(() => {
    if (channelError) {
      console.log(`Error with ${channelName}: ${channelError.message} (${channelError.code})`);

      if (channelError.code === 80017) {
        // Channel close is issued always prior to unload and is not actionable
        // https://faqs.ably.com/error-code-80017-connection-already-closed
        return;
      }

      if (channelError.code === 90001) {
        // Channel close is issued always prior to unload and is not actionable
        // https://faqs.ably.com/error-code-90001-channel-detached
        return;
      }

      addMessage('error', `Error subscribing to ${channelName} channel`);
    }
  }, [addMessage, channelError, channelName]);

  useEffect(() => {
    if (connectionError) {
      console.log(`Error connecting to ${channelName} channel`, connectionError);
      addMessage('error', `Error connecting to ${channelName} channel`);
    }
  }, [addMessage, channelName, connectionError]);

  return { ably, channel, channelData, channelError, connectionError };
};
